/**
 * TERMINAL command layer (ADR-0025 item 3).
 *
 * Every command is a shortcut to an action the GUI already offers. This module
 * never computes a price: quotes and trades go through injected adapters that
 * the view wires to the same coreSession store functions the MARKET app uses.
 * The server stays the authority; the terminal only parses and prints.
 *
 * Pure of canvas/DOM so the parser and runner are unit-testable.
 */

export const COMMANDS = ['help', 'status', 'prices', 'buy', 'sell', 'apps', 'open', 'ledger', 'evidence', 'ls', 'cat', 'clear', 'exit'];

const ALIASES = {
  '?': 'help',
  man: 'help',
  quote: 'prices',
  market: 'prices',
  loans: 'ledger',
  dir: 'ls',
  type: 'cat',
  cls: 'clear',
  quit: 'exit',
  logout: 'exit',
};

// Street names the player may type that are not the content-pack street name.
const ASSET_ALIASES = {
  ludes: 'quaaludes',
  quaalude: 'quaaludes',
  lude: 'quaaludes',
  coke: 'cocaine',
  bio: 'weed',
};

export const HELP_LINES = [
  'DOPEOS TERMINAL // COMMANDS',
  '  help                 this list',
  '  status               cash, debt, heat, day, district',
  '  prices               current street quotes (MARKET)',
  '  buy <asset> <qty>    buy at the quoted price',
  '  sell <asset> <qty>   sell at the quoted price',
  '  sell <asset> all     sell everything you hold of it',
  '  apps                 installed apps',
  '  open <app>           open an installed app',
  '  ledger | loans       your notes with Vinnie (LEDGER)',
  '  evidence             drops on file — open the EVIDENCE app',
  '  ls [path]            list files   (try ls /var/log)',
  '  cat <file>           read a file  (try cat ~/expediente/README.txt)',
  '  clear                wipe the screen',
  '  exit                 back to the desktop',
  'Assets: weed, ludes, speed, cocaine.  Up/down = history.',
  'Every command here has a button in the apps. Nothing is terminal-only.',
];

const out = (t) => ({ t, k: 'out' });
const dim = (t) => ({ t, k: 'dim' });
const err = (t) => ({ t, k: 'err' });

/** Split a raw line into a canonical command name and its arguments. */
export function parseCommand(line) {
  const raw = String(line ?? '').trim();
  if (!raw) return { raw, cmd: '', args: [] };
  const [first, ...args] = raw.split(/\s+/);
  const lower = first.toLowerCase();
  const cmd = ALIASES[lower] || lower;
  return { raw, cmd, args };
}

/**
 * Parse a quantity token. Returns `{ qty }`, `{ all: true }` (sell only) or
 * `{ error }`. Quantities are whole positive numbers; bounds are the server's.
 */
export function parseQty(token, { allowAll = false } = {}) {
  if (token == null || token === '') return { qty: 1 };
  const lower = String(token).toLowerCase();
  if (lower === 'all') return allowAll ? { all: true } : { error: 'ALL ONLY WORKS WITH SELL' };
  if (!/^\d+$/.test(lower)) return { error: `BAD QUANTITY: ${token}` };
  const qty = Number.parseInt(lower, 10);
  if (!Number.isSafeInteger(qty) || qty < 1) return { error: 'QUANTITY MUST BE 1 OR MORE' };
  return { qty };
}

/** Resolve a typed asset name (id, street name, alias) against tradable assets. */
export function resolveAsset(token, assets = []) {
  if (!token) return null;
  const key = String(token).toLowerCase().replace(/[^a-z0-9_]/g, '');
  const wanted = ASSET_ALIASES[key] || key;
  return (
    assets.find((a) => String(a.id).toLowerCase() === wanted)
    || assets.find((a) => String(a.streetName || '').toLowerCase() === wanted)
    || assets.find((a) => String(a.symbol || '').toLowerCase() === wanted)
    || null
  );
}

/** Resolve `market`, `market.app`, `MARKET` to a catalog entry. */
export function resolveApp(token, catalog = []) {
  if (!token) return null;
  const key = String(token).toLowerCase().replace(/\.app$/, '');
  return catalog.find((a) => a.id === key || a.label.toLowerCase() === key) || null;
}

const money = (m) => (m?.amount == null ? '--' : `$${m.amount}`);
const pad = (v, n) => String(v).padEnd(n).slice(0, n);

function needApp(deps, appId) {
  if (deps.isInstalled(appId)) return null;
  const app = deps.catalog.find((a) => a.id === appId);
  return [err(`${app ? app.file : `${appId}.app`}: NOT INSTALLED ON THIS MACHINE`)];
}

async function tradeOne(deps, asset, side, qty) {
  const quote = await deps.quote(asset.id, side, qty);
  const lines = [
    dim(`QUOTE ${side.toUpperCase()} ${qty} ${asset.name} @ ${money(quote.unitPrice)} = ${money(quote.total)} // HEAT +${quote.heatDelta ?? 0}`),
  ];
  if (!quote.canExecute) {
    const reason = quote.disabledReasons?.[0]?.message || 'TRADE REFUSED';
    lines.push(err(`REFUSED: ${String(reason).toUpperCase()}`));
    return lines;
  }
  const result = await deps.execute(asset.id, side, qty);
  const trade = result?.trade || {};
  lines.push(out(`DONE ${side.toUpperCase()} ${qty} ${asset.name} // ${money(trade.total)} // HEAT +${trade.heatDelta ?? 0}`));
  return lines;
}

async function cmdTrade(side, args, deps) {
  const gate = needApp(deps, 'market');
  if (gate) return gate;
  const assets = await deps.getAssets();

  // `sell all` with no asset: liquidate every held public asset.
  if (side === 'sell' && args.length === 1 && String(args[0]).toLowerCase() === 'all') {
    const held = assets.filter((a) => deps.owned(a.id) > 0);
    if (!held.length) return [err('NOTHING IN THE COAT TO SELL')];
    const lines = [];
    for (const asset of held) lines.push(...await tradeOne(deps, asset, 'sell', deps.owned(asset.id)));
    return lines;
  }

  if (!args.length) return [err(`USAGE: ${side} <asset> <qty>`)];
  const asset = resolveAsset(args[0], assets);
  if (!asset) return [err(`UNKNOWN ASSET: ${args[0]}`), dim('TRY: weed, ludes, speed, cocaine')];
  const parsed = parseQty(args[1], { allowAll: side === 'sell' });
  if (parsed.error) return [err(parsed.error)];
  let qty = parsed.qty;
  if (parsed.all) {
    qty = deps.owned(asset.id);
    if (qty < 1) return [err(`NO ${asset.name} IN THE COAT`)];
  }
  return tradeOne(deps, asset, side, qty);
}

async function cmdPrices(deps) {
  const gate = needApp(deps, 'market');
  if (gate) return gate;
  const assets = await deps.getAssets();
  const lines = [out(`${pad('ASSET', 12)}${pad('BUY', 10)}${pad('SELL', 10)}OWN`)];
  for (const asset of assets) {
    const [buy, sell] = await Promise.all([deps.quote(asset.id, 'buy', 1), deps.quote(asset.id, 'sell', 1)]);
    lines.push(out(`${pad(asset.name, 12)}${pad(money(buy.unitPrice), 10)}${pad(money(sell.unitPrice), 10)}${deps.owned(asset.id)}`));
  }
  return lines;
}

async function cmdStatus(deps) {
  const s = await deps.getStatus();
  if (!s.online) return [err('LINE DOWN // NO STATUS')];
  return [
    out(`CASH      $${Number(s.cash).toFixed(2)}`),
    out(`DEBT      $${Number(s.debt).toFixed(2)}`),
    out(`HEAT      ${s.heat}`),
    out(`DAY       ${s.day ?? '--'}`),
    out(`DISTRICT  ${s.district || '--'}`),
  ];
}

async function cmdLedger(deps) {
  const gate = needApp(deps, 'ledger');
  if (gate) return gate;
  const loans = await deps.loadLoans();
  if (!loans?.length) return [out('NO NOTES ON FILE')];
  return [
    out(`${pad('NOTE', 10)}${pad('STATUS', 10)}${pad('PRINCIPAL', 11)}DUE`),
    ...loans.map((l) => out(`${pad(String(l.loanId || '--').slice(-8), 10)}${pad(String(l.status || '--').toUpperCase(), 10)}${pad(money(l.principal), 11)}${money(l.amountDue)}`)),
  ];
}

function cmdEvidence(deps) {
  if (!deps.isInstalled('evidence')) return needApp(deps, 'evidence');
  deps.navigate('evidence');
  return [out('OPENING EVIDENCE.APP')];
}

function cmdApps(deps) {
  const installed = deps.catalog.filter((a) => deps.isInstalled(a.id));
  return [out('INSTALLED'), ...installed.map((a) => out(`  ${pad(a.file, 16)}${a.label}`))];
}

function cmdOpen(args, deps) {
  if (!args.length) return [err('USAGE: open <app>')];
  const app = resolveApp(args[0], deps.catalog);
  if (!app) return [err(`open: ${args[0]}: NO SUCH APP`)];
  if (!deps.isInstalled(app.id)) return [err(`${app.file}: NOT INSTALLED ON THIS MACHINE`)];
  if (app.id === 'terminal') return [dim('terminal.app: ALREADY RUNNING')];
  deps.navigate(app.view);
  return [out(`OPENING ${app.file.toUpperCase()}`)];
}

/**
 * Run one command line. Resolves to `{ lines, clear?, exit? }`. Never throws:
 * server/transport failures become in-fiction error lines.
 */
export async function runCommand(line, deps) {
  const { cmd, args } = parseCommand(line);
  if (!cmd) return { lines: [] };
  try {
    switch (cmd) {
      case 'help': return { lines: HELP_LINES.map((t, i) => (i === 0 ? out(t) : dim(t))) };
      case 'status': return { lines: await cmdStatus(deps) };
      case 'prices': return { lines: await cmdPrices(deps) };
      case 'buy': return { lines: await cmdTrade('buy', args, deps) };
      case 'sell': return { lines: await cmdTrade('sell', args, deps) };
      case 'apps': return { lines: cmdApps(deps) };
      case 'open': return { lines: cmdOpen(args, deps) };
      case 'ledger': return { lines: await cmdLedger(deps) };
      case 'evidence': return { lines: cmdEvidence(deps) };
      case 'ls': return { lines: await deps.fs.ls(args) };
      case 'cat': return { lines: await deps.fs.cat(args) };
      case 'clear': return { lines: [], clear: true };
      case 'exit': deps.navigate('main_menu'); return { lines: [dim('LINE CLOSED')], exit: true };
      default:
        return { lines: [err(`${cmd}: COMMAND NOT FOUND`), dim('TYPE help')] };
    }
  } catch (error) {
    return { lines: [err(deps.errMsg ? deps.errMsg(error) : (error?.message || 'LINE DOWN'))] };
  }
}
